/* istanbul ignore file */

import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import loadable from '@loadable/component';

import AppHooks from './AppHooks';
import {
	getDealsStub,
	getStub,
	gotLister,
	gotProductsInBasket,
} from './actions/lister-actions';
import Container from './components/Container';
import FinanceSlideoutComponent from './components/FinanceSlideoutComponent';
import LoadingSpinner from './components/LoadingSpinner/LoadingSpinner';
import useCompare from './hooks/useCompare';
import { useEventListener } from './hooks/useEventListener';
import { useExperimentSwitch } from './hooks/useExperimentSwitch';
import { checkBasket } from './utilities/basket-utilities';
import { translateQueryString } from './utilities/lister-utilities';

const SlideOutDrawerWrapper = loadable(() =>
	import(
		/* webpackPrefetch: true */ './components/SlideOutDrawer/SlideOutDrawerWrapper'
	)
);

const App = () => {
	const state = useSelector((_state) => _state);

	const {
		drawer: { initialized },
		products,
		loading,
	} = state;

	const dispatch = useDispatch();

	useExperimentSwitch();
	useCompare();

	useEventListener('popstate', () => {
		window.location.reload();
	});

	function skipToMainContent(event) {
		event.preventDefault();

		const target = document.getElementById('lister-header');

		if (target) {
			target.scrollIntoView({ behavior: 'smooth' });
			target.setAttribute('tabindex', '-1');
			target.focus();
		}
	}

	const skipLink = document.getElementById('header-skip-link');
	skipLink?.addEventListener('click', skipToMainContent);

	useEffect(() => {
		// get data
		try {
			if (window.location.href.includes('?stub=true')) {
				dispatch(getStub());
			} else if (window.location.href.includes('?dealsStub=true')) {
				dispatch(getDealsStub());
			} else {
				const initialListerDataBlob = document.getElementById('lister-data');
				const data = JSON.parse(initialListerDataBlob.innerHTML);
				dispatch(gotLister(data));
			}
		} catch (error) {
			throw new Error(error);
		}

		const newUrl = translateQueryString(window.location.href);
		history.replaceState(history.state, null, newUrl);

		const productsInBasket = checkBasket();

		if (productsInBasket) {
			dispatch(gotProductsInBasket(productsInBasket));
		}
	}, [dispatch]);

	if (products.length <= 0 && loading) {
		return null;
	}

	return (
		<React.Fragment>
			<Container {...state} />
			<FinanceSlideoutComponent />
			{initialized && <SlideOutDrawerWrapper />}
			<AppHooks />
			{loading && <LoadingSpinner />}
		</React.Fragment>
	);
};

export default App;
